/**
 * useOptions – Options analysis store (Zustand).
 * Manages chain, max pain, unusual activity and IV rank for a ticker.
 */
import { create } from 'zustand'
import {
  getOptionsExpirations,
  getOptionsChain,
  getOptionsMaxPain,
  getOptionsUnusual,
  getOptionsIVRank,
} from '@/lib/api'
import type {
  OptionsChainResponse,
  MaxPainResponse,
  UnusualActivityResponse,
  IVRankResponse,
} from '@/lib/types'

interface OptionsState {
  ticker: string
  expiry: string       // currently selected expiry date
  expirations: string[]

  chain: OptionsChainResponse | null
  maxPain: MaxPainResponse | null
  unusual: UnusualActivityResponse | null
  ivRank: IVRankResponse | null

  chainLoading: boolean
  maxPainLoading: boolean
  unusualLoading: boolean
  ivRankLoading: boolean

  chainError: string | null
  maxPainError: string | null
  unusualError: string | null
  ivRankError: string | null

  setTicker: (ticker: string) => void
  setExpiry: (expiry: string) => void

  fetchExpirations: (ticker: string) => Promise<void>
  fetchChain: (ticker: string, expiry?: string) => Promise<void>
  fetchMaxPain: (ticker: string, expiry?: string) => Promise<void>
  fetchUnusual: (ticker: string) => Promise<void>
  fetchIVRank: (ticker: string) => Promise<void>

  /** Convenience: load all data for a ticker */
  loadAll: (ticker: string, expiry?: string) => Promise<void>
}

export const useOptions = create<OptionsState>()((set, get) => ({
  ticker: '',
  expiry: '',
  expirations: [],

  chain: null,
  maxPain: null,
  unusual: null,
  ivRank: null,

  chainLoading: false,
  maxPainLoading: false,
  unusualLoading: false,
  ivRankLoading: false,

  chainError: null,
  maxPainError: null,
  unusualError: null,
  ivRankError: null,

  setTicker: (ticker) => set({ ticker }),
  setExpiry: (expiry) => set({ expiry }),

  fetchExpirations: async (ticker) => {
    try {
      const res = await getOptionsExpirations(ticker)
      set({
        expirations: res.expirations,
        expiry: res.expirations[0] ?? '',
      })
    } catch (e) {
      set({ expirations: [] })
    }
  },

  fetchChain: async (ticker, expiry) => {
    set({ chainLoading: true, chainError: null })
    try {
      const chain = await getOptionsChain(ticker, expiry || get().expiry || undefined)
      set({ chain, chainLoading: false, expiry: chain.expiration })
    } catch (e) {
      set({ chainError: String(e), chainLoading: false })
    }
  },

  fetchMaxPain: async (ticker, expiry) => {
    set({ maxPainLoading: true, maxPainError: null })
    try {
      const maxPain = await getOptionsMaxPain(ticker, expiry || get().expiry || undefined)
      set({ maxPain, maxPainLoading: false })
    } catch (e) {
      set({ maxPainError: String(e), maxPainLoading: false })
    }
  },

  fetchUnusual: async (ticker) => {
    set({ unusualLoading: true, unusualError: null })
    try {
      const unusual = await getOptionsUnusual(ticker)
      set({ unusual, unusualLoading: false })
    } catch (e) {
      set({ unusualError: String(e), unusualLoading: false })
    }
  },

  fetchIVRank: async (ticker) => {
    set({ ivRankLoading: true, ivRankError: null })
    try {
      const ivRank = await getOptionsIVRank(ticker)
      set({ ivRank, ivRankLoading: false })
    } catch (e) {
      set({ ivRankError: String(e), ivRankLoading: false })
    }
  },

  loadAll: async (ticker, expiry) => {
    const t = ticker.trim().toUpperCase()
    if (!t) return
    set({ ticker: t, chain: null, maxPain: null, unusual: null, ivRank: null })

    // Fetch expirations first so we know which date to use
    await get().fetchExpirations(t)
    const resolvedExpiry = expiry || get().expirations[0] || undefined

    // Parallel load of remaining data
    await Promise.allSettled([
      get().fetchChain(t, resolvedExpiry),
      get().fetchMaxPain(t, resolvedExpiry),
      get().fetchUnusual(t),
      get().fetchIVRank(t),
    ])
  },
}))
